import { Injectable } from '@angular/core';
import { from } from 'jalali-moment';

const DATE_PATTERN = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?=$|T|\s)/;

/** Leading `YYYY`, `MM` and `DD` of a date string, padded and slash separated. */
function splitDate(value: string | null | undefined): { year: number; padded: string } | null {
 const match = DATE_PATTERN.exec(String(value ?? '').trim());
 if (!match) return null;

 return {
  year: +match[1],
  padded: `${match[1]}/${match[2].padStart(2, '0')}/${match[3].padStart(2, '0')}`,
 };
}

/**
 * A Gregorian year is always far above any Jalali year in use, so the year alone
 * identifies which calendar a date string belongs to.
 */
export function isGregorianDate(value: string | null | undefined): boolean {
 const parts = splitDate(value);
 return !!parts && parts.year > 1700;
}

/**
 * Jalali `YYYY/MM/DD`. Accepts Gregorian `YYYY-MM-DD` or full ISO, and passes Jalali
 * through unchanged so it stays correct when applied more than once. Returns '' when
 * the value cannot be parsed.
 */
export function toJalaliDate(value: string | null | undefined): string {
 const parts = splitDate(value);
 if (!parts) return '';
 const calendar = parts.year > 1700 ? 'en' : 'fa';
 const parsed = from(parts.padded, calendar, 'YYYY/MM/DD');
 if (!parsed.isValid() || parsed.locale(calendar).format('YYYY/MM/DD') !== parts.padded) {
  return '';
 }
 return parsed.locale('fa').format('YYYY/MM/DD');
}

/**
 * Gregorian `YYYY-MM-DD` as the API expects it. Accepts Jalali `YYYY/MM/DD` or an
 * already Gregorian value, so it stays correct when applied more than once. Returns ''
 * when the value cannot be parsed.
 */
export function toGregorianApiDate(value: string | null | undefined): string {
 const parts = splitDate(value);
 if (!parts) return '';
 const calendar = parts.year > 1700 ? 'en' : 'fa';
 const parsed = from(parts.padded, calendar, 'YYYY/MM/DD');
 if (!parsed.isValid() || parsed.locale(calendar).format('YYYY/MM/DD') !== parts.padded) {
  return '';
 }
 return parsed.locale('en').format('YYYY-MM-DD');
}

/** Current calendar day in Tehran, shared by SSR and browser defaults. */
export function todayGregorianIso(): string {
 const parts = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Tehran',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
 }).formatToParts(new Date());
 const part = (type: string) => parts.find((value) => value.type === type)!.value;
 return `${part('year')}-${part('month')}-${part('day')}`;
}

/** Jalali today (`YYYY/MM/DD`), optionally from a Gregorian `YYYY-MM-DD` base day. */
export function todayJalali(gregorianDate?: string): string {
 return toJalaliDate(gregorianDate || todayGregorianIso());
}

/** Jalali tomorrow (`YYYY/MM/DD`), optionally from a Gregorian `YYYY-MM-DD` base day. */
export function tomorrowJalali(gregorianDate?: string): string {
 const base = gregorianDate || todayGregorianIso();
 const next = from(base, 'en', 'YYYY-MM-DD').add(1, 'day').locale('en').format('YYYY-MM-DD');
 return toJalaliDate(next);
}

@Injectable({
 providedIn: 'root',
})
export class MomentService {
 constructor() {}

 toGergorian(jalaliDate: string) {
  const newDate = new Date().toTimeString();
  const gregorianDate = from(`${jalaliDate} ${newDate}`, 'fa', 'YYYY/MM/DD HH:mm:ss').toISOString();
  return new Date(gregorianDate);
 }

 convertISOtoJalali(gregorianIsoDate: string): string {
  return toJalaliDate(gregorianIsoDate);
 }
 getTodayAndTomorrowJalali(): { startDate: string; endDate: string } {
  const today = todayGregorianIso();
  return { startDate: todayJalali(today), endDate: tomorrowJalali(today) };
 }
}
